/**
 * ShotAnalyzerManual — mandatory pre-use manual modal.
 *
 * Shown automatically the first time a user opens the Shot Analyzer
 * (we remember the dismissal in localStorage). Also reachable from a
 * "Manual" button inside the module header. The "Entendido y acepto"
 * button is the only way to dismiss the first-time view.
 */
import React from 'react';
import { Camera, Sun, Ruler, Target, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { useLang } from '@/context/LangContext';

export const SHOT_MANUAL_ACK_KEY = 'sa_manual_ack_v1';

export default function ShotAnalyzerManual({ onClose, onAck, mandatory }) {
  const { t } = useLang();
  const items = [
    { icon: Camera,         titleKey: 'sa_man_camera_title',     bodyKey: 'sa_man_camera_body' },
    { icon: Ruler,          titleKey: 'sa_man_distance_title',   bodyKey: 'sa_man_distance_body' },
    { icon: Sun,            titleKey: 'sa_man_light_title',      bodyKey: 'sa_man_light_body' },
    { icon: Target,         titleKey: 'sa_man_align_title',      bodyKey: 'sa_man_align_body' },
    { icon: CheckCircle2,   titleKey: 'sa_man_good_title',       bodyKey: 'sa_man_good_body' },
    { icon: AlertTriangle,  titleKey: 'sa_man_bad_title',        bodyKey: 'sa_man_bad_body' },
  ];

  return (
    <div
      data-testid="sa-manual-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={() => { if (!mandatory) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] rounded-xl border-2 border-gold/40 bg-navy-deep flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gold/20">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('sa_manual_eyebrow')}</div>
            <h2 className="text-xl font-h text-white mt-1">{t('sa_manual_title')}</h2>
          </div>
          {!mandatory && (
            <button
              data-testid="sa-manual-close"
              onClick={onClose}
              className="p-2 rounded-md hover:bg-white/5 text-slate-300"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <p className="text-sm text-slate-300 leading-relaxed">{t('sa_manual_intro')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-4 h-4 text-gold" />
                    <div className="text-[11px] font-mono uppercase tracking-widest text-white">{t(it.titleKey)}</div>
                  </div>
                  <div className="text-xs text-slate-300 leading-relaxed">{t(it.bodyKey)}</div>
                </div>
              );
            })}
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 leading-relaxed">
            <strong className="font-bold">{t('sa_manual_requirements_title')}:</strong> {t('sa_manual_requirements_body')}
          </div>
          <div className="rounded-lg border border-white/10 bg-black/40 p-3 text-[11px] text-slate-400 italic leading-relaxed">
            {t('sa_manual_disclaimer')}
          </div>
        </div>
        <div className="p-5 border-t border-gold/20 flex items-center justify-end gap-2">
          {!mandatory && (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest border border-white/15 text-slate-200 hover:bg-white/5"
            >
              {t('close')}
            </button>
          )}
          <button
            data-testid="sa-manual-accept"
            onClick={() => { onAck?.(); onClose?.(); }}
            className="px-5 py-2 rounded-md text-xs font-mono uppercase tracking-widest bg-gold text-navy font-bold hover:brightness-110"
          >
            {t('sa_manual_accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
